// import { AnimatePresence, motion } from "motion/react";
import { AnimatePresence, motion } from "motion/react";
import { FC, ReactElement, ReactNode, useMemo } from "react";
import { useRecoilValue } from "recoil";

import {
  FiltersActions,
  FiltersActionsApply,
  FloatingMenuGrid,
  TableWrapperGeneric,
  useTableColumnsDynamic,
} from "@ryde/controls";
import {
  BulkUpdateFormDrawer,
  ClientSideBulkOperationDrawerFlow,
  FinalSchemaService,
  PageHeader,
  PageHeaderFilters,
  TableSettingsDrawerWrapperGeneric,
} from "@ryde/layout";
import {
  IPageHeaderContext,
  PageHeaderContext,
  usePageContext,
  usePageTitle,
} from "@ryde/redux";
import {
  bulkOperationsDrawerAF,
  bulkUpdateFormDrawerAF,
  IColumnDef,
  IExtendableDto,
  isEntityServerSideFiltered,
  ITableDto,
  selectedTableRowsIdsAtomFamily,
  SPLITTER_MAP_PASSENGERS_INITIAL_WIDTH,
  tableSettingsDrawerAF,
} from "@ryde/shared";

import { usePassengerGridSplitter, usePassengersPage } from "./hooks";
import {
  PassengersGridMap,
  PassengersGridTableWrapperStyled,
  PassengersPanelTop,
} from "./panels";

interface IPassengersPageContentProps {
  customRightComponent?: ReactElement | null;
  customHeaderBottomRightActions?: ReactNode;
  topWidgetComponent?: ReactNode;
}

export const PassengersPageContent: FC<IPassengersPageContentProps> = ({
  customRightComponent,
  customHeaderBottomRightActions,
  topWidgetComponent,
}) => {
  const { entity } = usePageContext();
  const isServerSideFilteredEntity = isEntityServerSideFiltered(entity);

  const tableSettingsDrawerOpened = useRecoilValue(
    tableSettingsDrawerAF(entity),
  );
  const bulkOperationsDrawerOpened = useRecoilValue(
    bulkOperationsDrawerAF(entity),
  );
  const selectedIds = useRecoilValue(selectedTableRowsIdsAtomFamily(entity));
  const bulkUpdateFormDrawerOpened = useRecoilValue(
    bulkUpdateFormDrawerAF(entity),
  );
  const { tableDefsGeneric } = useTableColumnsDynamic();
  const { pageTitleTranslated } = usePageTitle();
  const { onFiltersApply, onScrollEnd } = usePassengersPage();

  const headerContextValue: IPageHeaderContext = useMemo(() => {
    return {
      onFiltersApply,
      onFiltersClear: onFiltersApply,
      onSearchApply: onFiltersApply,
    };
  }, [onFiltersApply]);

  const {
    mapOpen,
    x,
    onDragStartHandler,
    onDragEndHandler,
    gridSizeProps,
    mapSizeProps,
    dragConstraints,
  } = usePassengerGridSplitter(SPLITTER_MAP_PASSENGERS_INITIAL_WIDTH);

  return (
    <>
      <FinalSchemaService />
      <PageHeader
        mainTitle={pageTitleTranslated}
        customRightComponent={customRightComponent}
        customHeaderBottomRightActions={customHeaderBottomRightActions}
        topWidgetComponent={topWidgetComponent}
        bottomWidgetComponent={<PassengersPanelTop />}
      >
        <PageHeaderContext.Provider value={headerContextValue}>
          <PageHeaderFilters
            Actions={
              isServerSideFilteredEntity ? (
                <FiltersActionsApply />
              ) : (
                <FiltersActions />
              )
            }
          />
        </PageHeaderContext.Provider>
      </PageHeader>

      <PassengersGridTableWrapperStyled>
        <motion.div className="grid_panel thin_scrollbar" style={gridSizeProps}>
          <TableWrapperGeneric
            tableDefs={
              tableDefsGeneric as IColumnDef<IExtendableDto, unknown>[]
            }
            onScrollEnd={onScrollEnd}
          />
        </motion.div>

        <AnimatePresence>
          {mapOpen && (
            <motion.div
              className="splitter"
              key="splitter"
              dragElastic={0}
              dragMomentum={false}
              onDragStart={onDragStartHandler}
              onDragEnd={onDragEndHandler}
              dragConstraints={dragConstraints}
              drag="x"
              style={{
                cursor: "col-resize",
                x,
              }}
            />
          )}

          {mapOpen && (
            <motion.div
              key="map_panel"
              className="map_panel"
              style={mapSizeProps}
            >
              <PassengersGridMap />
            </motion.div>
          )}
        </AnimatePresence>
      </PassengersGridTableWrapperStyled>

      {tableSettingsDrawerOpened && (
        <TableSettingsDrawerWrapperGeneric<ITableDto>
          tableDefs={tableDefsGeneric}
        />
      )}
      {selectedIds && <FloatingMenuGrid />}
      {bulkUpdateFormDrawerOpened && <BulkUpdateFormDrawer />}

      {bulkOperationsDrawerOpened && <ClientSideBulkOperationDrawerFlow />}
    </>
  );
};
